import type { FunctionComponent } from 'react';

import {
  Accordion,
  Anchor,
  Avatar,
  Badge,
  Button,
  Divider,
  Group,
  Stack,
  Text,
} from '@mantine/core';

import { BADGE_VARIANTS } from 'constants/mantineVariants';
import { PUBLISH_ERROR_MESSAGE, PUBLISH_NOTICE } from 'constants/menuManagement';
import { INTEGRATION_NAME_MAP } from 'constants/reporting';

import NotificationWrapper from 'primitives/NotificationWrapper/NotificationWrapper';
import SourceAvatar from 'components/SourceAvatar';

import styles from 'styles/theme.module.scss';

import type { PublishResult } from '../types';

type ResultsStepProps = {
  gap: string;
  publishResult: PublishResult | null;
  publishHasError: boolean;
  publishErrorMessage?: string | null;
  handleDoneClick: () => void;
};

const NOTIFICATION_STYLES = {
  root: { marginTop: 0 },
  description: {
    height: 'auto',
    whiteSpace: 'normal' as const,
    overflow: 'visible',
    textOverflow: 'unset',
    lineHeight: 'var(--line-height-normal)',
    flexWrap: 'wrap' as const,
  },
};

export const ResultsStep: FunctionComponent<ResultsStepProps> = ({
  gap,
  publishResult,
  publishHasError,
  publishErrorMessage,
  handleDoneClick,
}) => {
  const integrations = publishResult?.responses ?? [];
  const integrationsHasFail = integrations.some(integration => integration.status_code !== 200);

  if (publishHasError) {
    return (
      <Stack gap={gap}>
        <NotificationWrapper size="compact" variant="caution" styles={NOTIFICATION_STYLES}>
          {publishErrorMessage || PUBLISH_ERROR_MESSAGE}
        </NotificationWrapper>
        <Button onClick={handleDoneClick} fullWidth>
          Done
        </Button>
      </Stack>
    );
  }

  return (
    <Stack gap={gap}>
      <NotificationWrapper
        size="compact"
        variant={integrationsHasFail ? 'caution' : 'success'}
        styles={NOTIFICATION_STYLES}
      >
        {integrationsHasFail ? PUBLISH_ERROR_MESSAGE : PUBLISH_NOTICE}
      </NotificationWrapper>
      {integrations.map(integration => (
        <Accordion variant="bordered-card" key={integration.integration_name}>
          <Accordion.Item value={integration.integration_name}>
            <Accordion.Control>
              <Group gap="xs2" wrap="nowrap">
                <Avatar variant="outline" size="md">
                  <SourceAvatar source={integration.integration_name} />
                </Avatar>
                {integration.status_code === 200 ? (
                  <Text variant="title-sm">
                    {(INTEGRATION_NAME_MAP as Record<string, string>)[
                      integration.integration_name
                    ] || integration.integration_name}
                    : Merchants (1), Menus ({integration.menu_names?.length || 0})
                  </Text>
                ) : (
                  <Badge variant={BADGE_VARIANTS.caution} className={styles.publishMenuStatusBadge}>
                    Fail
                  </Badge>
                )}
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              {integration.status_code !== 200 ? (
                <Text>
                  The publish process failed for an unknown reason. Please contact{' '}
                  <Anchor href="mailto:support@cuboh.com">support@cuboh.com</Anchor>.
                </Text>
              ) : (
                <Stack>
                  <Text variant="title-sm">{integration.company_name}</Text>
                  {integration.menu_names.map((menu, index) => (
                    <Stack key={menu}>
                      <Divider variant="dashed" />
                      <Stack gap="xs3">
                        <Text variant="label-sm">{menu}</Text>
                        <Text variant="sublabel-xs" c="var(--content-secondary)">
                          {integration.menu_uuids[index]}
                        </Text>
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      ))}
      <Button onClick={handleDoneClick} fullWidth>
        Done
      </Button>
    </Stack>
  );
};
